import { apiRequest } from '../../shared/api/client';

/**
 * Inventory Planning — per-warehouse item settings, mirroring
 * Invetrak.Api.Endpoints.ItemWarehousePlanningEndpoints (camelCase).
 *
 *   GET /api/v1/warehouses/{warehouseId}/item-planning
 *   GET /api/v1/warehouses/{warehouseId}/item-planning/{itemId}
 *   PUT /api/v1/warehouses/{warehouseId}/item-planning/{itemId}
 *   GET /api/v1/warehouses/{warehouseId}/item-planning/{itemId}/suggested-min-max
 *
 * Reads need items:read, writes items:write (enforced server-side).
 *
 * Why this is its own area rather than part of the item screen: the same item has
 * different planning in every warehouse, so there is no single value to show on an
 * item. That is how the current product works, and it is gap-analysis decision D3.
 */

export interface ItemPlanningDto {
  itemId: string;
  warehouseId: string;
  itemNumber: string;
  description: string;
  stockingUomCode: string;
  isStocked: boolean;
  minQuantity: number | null;
  maxQuantity: number | null;
  leadTimeDays: number | null;
  orderIncrement: number | null;
  /** Weeks of movement history the suggestion averages over. */
  weeksOfHistory: number | null;
  /** Extra weeks of cover to carry above the minimum. */
  weeksOverMinimum: number | null;
  promptReorder: boolean;
  reorderFlag: boolean;
  countTolerance: number | null;
  isCountControlGroup: boolean;
  countPeriodDays: number | null;
  /** Written by counting, not editable here. */
  lastCountedAtUtc: string | null;
  /** Written by the scheduler, not editable here. */
  nextCountDueAtUtc: string | null;
  /**
   * Derived from the latest ABC snapshot, never stored on the planning row and never
   * editable — it is calculated from inventory valuation. Null until the
   * classification job has run.
   */
  currentAbcClass: string | null;
  abcClassifiedAtUtc: string | null;
}

/**
 * A complete replacement of the settable fields — the endpoint is a PUT, so an omitted
 * value clears rather than preserves. Cycle-count scheduling is absent by design: it
 * belongs to counting and the scheduler.
 */
export interface UpsertPlanningRequest {
  isStocked: boolean;
  minQuantity: number | null;
  maxQuantity: number | null;
  leadTimeDays: number | null;
  orderIncrement: number | null;
  weeksOfHistory: number | null;
  weeksOverMinimum: number | null;
  promptReorder: boolean;
  reorderFlag: boolean;
  countTolerance: number | null;
  isCountControlGroup: boolean;
  countPeriodDays: number | null;
}

export interface SuggestedMinMaxDto {
  suggestedMinQuantity: number;
  suggestedMaxQuantity: number;
  /** Average quantity issued per week over the window, in the stocking unit. */
  weeklyAverageIssued: number;
  weeksOfHistory: number;
  leadTimeDays: number;
  weeksOverMinimum: number;
}

const COOKIE_INIT: RequestInit = { credentials: 'include' };

function base(warehouseId: string): string {
  return `/v1/warehouses/${encodeURIComponent(warehouseId)}/item-planning`;
}

export async function listItemPlanning(
  warehouseId: string,
  includeInactive = false,
  signal?: AbortSignal,
): Promise<ItemPlanningDto[]> {
  const query = includeInactive ? '?includeInactive=true' : '';
  const { data } = await apiRequest<ItemPlanningDto[]>(`${base(warehouseId)}/${query}`, {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function upsertItemPlanning(
  warehouseId: string,
  itemId: string,
  body: UpsertPlanningRequest,
  signal?: AbortSignal,
): Promise<ItemPlanningDto> {
  const { data } = await apiRequest<ItemPlanningDto>(
    `${base(warehouseId)}/${encodeURIComponent(itemId)}`,
    { method: 'PUT', body, signal, ...COOKIE_INIT },
  );
  return data;
}

export async function getSuggestedMinMax(
  warehouseId: string,
  itemId: string,
  params: { weeksOfHistory?: number | null; leadTimeDays?: number | null; weeksOverMinimum?: number | null } = {},
  signal?: AbortSignal,
): Promise<SuggestedMinMaxDto> {
  const query = new URLSearchParams();
  // Omitted parameters fall back to whatever is stored for the item, then to the
  // server's defaults — so only send what was actually supplied.
  if (params.weeksOfHistory != null) query.set('weeksOfHistory', String(params.weeksOfHistory));
  if (params.leadTimeDays != null) query.set('leadTimeDays', String(params.leadTimeDays));
  if (params.weeksOverMinimum != null) query.set('weeksOverMinimum', String(params.weeksOverMinimum));

  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  const { data } = await apiRequest<SuggestedMinMaxDto>(
    `${base(warehouseId)}/${encodeURIComponent(itemId)}/suggested-min-max${suffix}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

/** Turns a planning row back into the request shape, so an edit starts from what is saved. */
export function toUpsertRequest(p: ItemPlanningDto): UpsertPlanningRequest {
  return {
    isStocked: p.isStocked,
    minQuantity: p.minQuantity,
    maxQuantity: p.maxQuantity,
    leadTimeDays: p.leadTimeDays,
    orderIncrement: p.orderIncrement,
    weeksOfHistory: p.weeksOfHistory,
    weeksOverMinimum: p.weeksOverMinimum,
    promptReorder: p.promptReorder,
    reorderFlag: p.reorderFlag,
    countTolerance: p.countTolerance,
    isCountControlGroup: p.isCountControlGroup,
    countPeriodDays: p.countPeriodDays,
  };
}
